import type { GlobalAssistantConversationTurn } from './global-ai-assistant-api';
import type { GlobalAiTranscriptMessage } from './global-ai-assistant-transcript';

/**
 * Compact summary of older transcript turns that have rolled out of the
 * recency window. Generated asynchronously by the
 * `useGlobalAiConversationSummarizer` hook against the
 * `summarize-conversation` endpoint, and threaded through to every
 * downstream prompt so the model can interpret short follow-ups even
 * after a long session.
 */
export type GlobalAiConversationSummary = {
  /**
   * Exclusive index in the transcript that the summary covers. The
   * compacted range is `transcript[0..upToTurnIndex - 1]`. Turns at or
   * after this index are sent to the backend verbatim.
   */
  upToTurnIndex: number;
  text: string;
  generatedAtMs: number;
};

/** Recent turns sent verbatim alongside any older-turn summary. */
export const RECENT_TURNS_WINDOW = 12;
/** Start summarizing once at least this many turns sit older than the window. */
export const SUMMARIZE_TRIGGER = 3;
/** Resummarize when this many additional turns roll out of the window. */
export const SUMMARY_REGEN_DELTA = 5;

const MAX_CONTENT_CHARS = 2000;

function truncateContent(content: string): string {
  const trimmed = content.trim();
  if (trimmed.length <= MAX_CONTENT_CHARS) {
    return trimmed;
  }
  return `${trimmed.slice(0, MAX_CONTENT_CHARS)}…`;
}

/**
 * Maps the in-memory transcript to compact turns for the global assistant API.
 * Omits chart preview payloads; summarizes assistant structured messages as text.
 */
export function transcriptToGlobalAssistantConversationTurns(
  transcript: GlobalAiTranscriptMessage[]
): GlobalAssistantConversationTurn[] {
  return (() => {
    const out: GlobalAssistantConversationTurn[] = [];
    for (const m of transcript) {
      if (m.role === 'user') {
        out.push({ role: 'user', content: truncateContent(m.text) });
        continue;
      }
      if (m.kind === 'text') {
        // Ephemeral progress narration is on-screen only — never sent to the
        // model as conversation history (it would repeat transient build
        // chatter back into the next turn). Skip without emitting a turn.
        if (m.ephemeral) {
          continue;
        }
        out.push({ role: 'assistant', content: truncateContent(m.text) });
        continue;
      }
      if (m.kind === 'clarification') {
        out.push({
          role: 'assistant',
          content: truncateContent(`Clarification: ${m.text}`),
        });
        continue;
      }
      if (m.kind === 'error') {
        out.push({
          role: 'assistant',
          content: truncateContent(`Error: ${m.text}`),
        });
        continue;
      }
      if (m.kind === 'single_chart') {
        // answerAsText messages are offer:page wiring metadata — no chart was
        // shown for them, and the grounded figure lives in the narration text
        // that follows (which IS serialized). Keep them out of model-visible
        // history so later turns don't reason from hidden wiring entries like
        // "direct headcount answer" (the companionContext-skip successor).
        if (m.answerAsText) {
          continue;
        }
        const line =
          m.intentSummary ??
          m.chartSubtitle ??
          'Generated a chart preview for the user.';
        out.push({ role: 'assistant', content: truncateContent(line) });
      }
    }
    return out;
  })();
}

/**
 * Output shape for `buildBoundedConversationContext` — the unified
 * conversation payload sent on every backend call. `turns` is the
 * recent window verbatim; `summary` (when present) compacts older
 * turns that have rolled out of the window.
 */
export type GlobalAssistantBoundedConversationContext = {
  turns: GlobalAssistantConversationTurn[];
  summary?: string;
};

function appendInlineUserTurn(args: {
  transcript: GlobalAiTranscriptMessage[];
  appendedUserTurn?: { text: string };
}): GlobalAiTranscriptMessage[] {
  const { transcript, appendedUserTurn } = args;
  if (!appendedUserTurn) {
    return transcript;
  }
  return [
    ...transcript,
    {
      id: '__inline__',
      role: 'user',
      text: appendedUserTurn.text,
    },
  ];
}

/**
 * Build the bounded conversation context sent on every global
 * assistant backend request: the most recent {@link RECENT_TURNS_WINDOW}
 * turns verbatim, plus a compacted summary of older turns when one has
 * been generated by `useGlobalAiConversationSummarizer`.
 *
 * Caller can provide an `appendedUserTurn` that hasn't yet been written
 * to the transcript state (covers the gap between submitting a prompt
 * and React re-rendering with the new transcript).
 *
 * Older turns that fall outside both the recency window and the
 * summary's coverage are still sent verbatim (we never silently drop a
 * turn while a summary is stale) — they get re-summarized once the
 * summarizer catches up.
 */
export function buildBoundedConversationContext(args: {
  transcript: GlobalAiTranscriptMessage[];
  summary: GlobalAiConversationSummary | null;
  appendedUserTurn?: { text: string };
}): GlobalAssistantBoundedConversationContext {
  const { transcript, summary, appendedUserTurn } = args;
  const fullTranscript = appendInlineUserTurn({ transcript, appendedUserTurn });

  if (fullTranscript.length === 0) {
    return { turns: [] };
  }

  const totalTurns = fullTranscript.length;
  const recentStart = Math.max(0, totalTurns - RECENT_TURNS_WINDOW);
  // Defensive clamp — a stale summary index (e.g. set against an older
  // / longer transcript) must never produce empty `turns` while we
  // still attach `summary.text`. Today the assistant context only grows
  // the transcript via `appendTranscript`, so this is latent; the clamp
  // protects against future code paths or external state hydration.
  const summaryUpToIndex = summary
    ? Math.max(0, Math.min(summary.upToTurnIndex, totalTurns))
    : null;
  // Keep summarized turns out of the verbatim block when the summary
  // covers older indices than the recency window already excludes.
  // Stale summary (covers fewer turns than the current older slice) is
  // still useful — we trust it for what it covers and let any uncovered
  // older turns ride along verbatim until the summarizer catches up.
  const verbatimStart =
    summaryUpToIndex !== null && summaryUpToIndex > recentStart
      ? summaryUpToIndex
      : recentStart;

  const verbatimSlice = fullTranscript.slice(verbatimStart);
  const turns = transcriptToGlobalAssistantConversationTurns(verbatimSlice);
  // Older-than-summary turns (if any) — happens when a stale summary
  // still covers fewer turns than `recentStart`. Prepend them verbatim
  // so we don't drop information just because summarization is behind.
  const olderUncoveredSlice =
    summaryUpToIndex !== null && summaryUpToIndex < recentStart
      ? fullTranscript.slice(summaryUpToIndex, recentStart)
      : [];
  const olderUncoveredTurns =
    transcriptToGlobalAssistantConversationTurns(olderUncoveredSlice);

  return {
    turns: [...olderUncoveredTurns, ...turns],
    ...(summary?.text ? { summary: summary.text } : {}),
  };
}
